import { EventConfigEnriched } from "src/clients/features-control";
import { useEventSchemas } from "src/eventsCatalogue/queries";
import { SchemaGlobalFeatures } from "src/schema/SchemaGlobalFeatures";
import { getEventSchemaOptions } from "src/schema/SchemaTypeSelector";
import {
  decodeJsonRefPath,
  isJsonRefPath,
  isReferencingEvent,
  JsonRefPath,
} from "src/schema/jsonRefUtils";
import { FieldAccessInfo } from "src/schema/schemaEditor/FieldAccessInfo";
import { SchemaTypeSelector } from "src/schema/schemaEditor/SchemaTypeSelector";
import { SchemaEditorProps } from "src/schema/schemaEditor/types";

const useSchemaData = () => {
  const { data: eventSchemasData } = useEventSchemas({
    enabled: true,
  });

  const schemasOptions = getEventSchemaOptions(eventSchemasData ?? []);

  return {
    eventSchemasData,
    schemasOptions,
  };
};

const useSelectedEvent = (
  formMethods: SchemaEditorProps["formMethods"],
  eventSchemasData: EventConfigEnriched[] | undefined,
) => {
  const properties = formMethods.getValues("properties") ?? [];
  const eventProperty = properties.find(
    (property) =>
      property.type &&
      isJsonRefPath(property.type[0]) &&
      isReferencingEvent(property.type[0] as JsonRefPath),
  );

  const selectedEventSchema = eventProperty?.type?.[0];
  const hasNoSchemas = !eventSchemasData || eventSchemasData.length === 0;

  const decodedRef = decodeJsonRefPath(selectedEventSchema ?? "");
  const selectedEvent =
    decodedRef?.metaType === "event"
      ? eventSchemasData?.find(
          (event) => event.internal_name === decodedRef.type,
        )
      : undefined;

  return {
    eventProperty,
    selectedEventSchema,
    hasNoSchemas,
    selectedEvent,
  };
};

export const EventSchemaEditor: React.FC<SchemaEditorProps> = ({
  formMethods,
  isReadonly,
}) => {
  const { eventSchemasData, schemasOptions } = useSchemaData();
  const { eventProperty, selectedEventSchema, hasNoSchemas, selectedEvent } =
    useSelectedEvent(formMethods, eventSchemasData);

  return (
    <>
      <SchemaTypeSelector
        formMethods={formMethods}
        hasNoSchemas={hasNoSchemas}
        isReadonly={isReadonly}
        schemasOptions={schemasOptions}
      />
      {selectedEventSchema && (
        <FieldAccessInfo
          inputType="event"
          schemaName={eventProperty?.fieldName ?? ""}
        />
      )}
      {eventSchemasData && selectedEvent && <SchemaGlobalFeatures />}
    </>
  );
};
